import PropTypes from "prop-types";
import React, { useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { IconButton, Button, Tooltip } from "@material-ui/core";
import DownloadIcon from "@material-ui/icons/GetApp";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { DateTime } from "luxon";
import API from "../../API";
import IndexTable from "../../components/Layout/IndexTable";
import { PromoContext } from "../../context";
import { PermissionedDisplay } from "../../components/Layout";
import Permissions from "../../permissions";

const useStyles = makeStyles(() => ({
  groupNameCell: {
    padding: 0,
    border: 0,
  },
}));

const PromoGroupsDisplay = ({ handleShowTabs }) => {
  const DEFAULT_TABLE_ROWS = 100;
  const theme = useTheme();
  const classes = useStyles(theme);
  const location = useLocation();
  const { t } = useTranslation();

  const { getPromoGroups, setActivePromoGroup, promoGroups, promoGroupsCount, searchPromoGroups } =
    useContext(PromoContext);

  const fetchPromoGroups = async ({ ordering, search, page, page_size }) =>
    searchPromoGroups(search, ordering, page_size, page);

  useEffect(() => {
    handleShowTabs(true);
    if (!promoGroups.length) {
      getPromoGroups();
    }
  }, []);

  const tableColumns = [
    {
      canonicalName: "name",
      renderCellContent: (row) => (
        <Button
          className={classes.groupNameCell}
          component={RouterLink}
          onClick={() => setActivePromoGroup(row)}
          to={{
            pathname: `/promo-groups/${row.id}`,
          }}
        >
          {row.name}
        </Button>
      ),
      sortable: true,
    },
    {
      canonicalName: "prefix",
      sortable: true,
    },
    {
      canonicalName: "edit",
      name: "",
      renderCellContent: (row) => (
        <PermissionedDisplay permission={[Permissions.readPromos, Permissions.writePromos]}>
          <Button
            component={RouterLink}
            to={{
              pathname: `/promo-groups/${row.id}/edit`,
              state: { nestedBackground: location },
            }}
          >
            {t("Promos.PromoSearch.Edit")}
          </Button>
        </PermissionedDisplay>
      ),
      permissions: [Permissions.readPromos, Permissions.writePromos],
    },
    {
      canonicalName: "export",
      name: "",
      tableCellProps: { padding: "none" },
      renderCellContent: (row) => (
        <Tooltip title={t("Promos.Links.ExportPromoCodes")}>
          <IconButton onClick={() => handleExport(row)}>
            <DownloadIcon />
          </IconButton>
        </Tooltip>
      ),
    },
  ].map((col) => ({
    ...col,
    name: col.name === "" ? col.name : t(`Promos.PromoGroups.Table.${col.canonicalName}`),
  }));

  const handleExport = async ({ id, name }) => {
    const response = await API.billing.exportCouponGroup(id);
    const blob = new Blob([response.data], { type: "text/csv" });

    const a = document.createElement("a");
    const today = DateTime.now().toFormat("yyyy-MM-dd");
    const fileName = `${name}-${today}`;

    a.download = fileName;
    a.href = window.URL.createObjectURL(blob);
    const clickEvt = new MouseEvent("click", {
      view: window,
      bubbles: true,
      cancelable: true,
    });
    a.dispatchEvent(clickEvt);
    a.remove();
  };

  return (
    <IndexTable
      muiTableProps={{
        size: "small",
        stickyHeader: true,
        "data-testid": "promo-groups-table",
      }}
      columns={tableColumns}
      rows={promoGroups}
      totalRowsCount={promoGroupsCount}
      defaultRowsPerPage={DEFAULT_TABLE_ROWS}
      fetchResource={fetchPromoGroups}
      searchProps={{
        placeholder: t("Promos.PromoSearch.SearchGroups"),
      }}
      createPath="/promo-groups/new"
      createPerms={[Permissions.readPromos, Permissions.writePromos]}
      createButtonText={t("Promos.Table.CreateGroup")}
    />
  );
};

PromoGroupsDisplay.propTypes = {
  handleShowTabs: PropTypes.func.isRequired,
};

export default PromoGroupsDisplay;
